import { useCallback, useState } from "react";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import { isValidId } from "@hooks/use-get/utils";
import { USER_FIELD_KEYS } from "@hooks/use-get/users/types";

export interface UpdateUserPayload {
  [USER_FIELD_KEYS.FIRST_NAME]?: string;
  [USER_FIELD_KEYS.LAST_NAME]?: string;
  [USER_FIELD_KEYS.STUDENT_ID]?: string | null;
  [USER_FIELD_KEYS.ROLE]?: string;
  [USER_FIELD_KEYS.GRADE]?: number | null;
  [USER_FIELD_KEYS.HOMEROOM]?: string | null;
  [USER_FIELD_KEYS.ENROLLMENT_STATUS]?: string;
}

const WP_ENDPOINT = "wp-json/smassets/v1/site-users";

export const useUpdateUser = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user } = useUserContext();
  const { getApiUrl } = useMultiSiteContext();
  const { refreshUserToken } = useRefreshTokenMutation();

  const updateUser = useCallback(
    async (wp_user_id: number, payload: UpdateUserPayload): Promise<boolean> => {
      if (!isValidId(wp_user_id)) {
        setError("Invalid user ID");
        return false;
      }

      setLoading(true);
      setError(null);

      try {
        const url = getApiUrl(`${WP_ENDPOINT}/${wp_user_id}`);
        const response = await fetchWithTokenRefresh(
          url,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${user?.authToken ?? ""}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          },
          () => refreshUserToken(user?.refreshToken ?? "")
        );

        if (!response.ok) {
          const body = await response.json().catch(() => null);
          const msg = body?.message ?? `Failed to update user (${response.status})`;
          setError(msg);
          setLoading(false);
          return false;
        }

        setLoading(false);
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to update user";
        setError(errorMessage);
        setLoading(false);
        return false;
      }
    },
    [user?.authToken, user?.refreshToken, getApiUrl, refreshUserToken]
  );

  const clearError = () => setError(null);

  return { updateUser, loading, error, clearError };
};
